/**
 * Quarter Icon — pixel-faithful port of `design/Quarter Icon.dc.html`
 * (DC-RUNTIME §6.4). This is the QuarterDown site mark: a pixel-art gold
 * quarter with an idle glint sweep, twinkling sparkles, one auto-spin every
 * ~8.1 s, and an arcade-speed spin on hover.
 *
 * Contract notes for the screen lanes:
 *  - Sprite maps, palette and every frame index are copied verbatim from the
 *    `.dc.html`. Do not "clean them up" — the frames are the artwork.
 *  - The clock is a single 90 ms interval created in `useEffect` with a
 *    `clearInterval` cleanup, so React 18 StrictMode's double-invoke in dev
 *    cannot leave a second interval running.
 *  - `tip` renders the component's own absolutely-positioned tooltip and is
 *    gated on the RAW hover state, so it still works when `hover={false}`
 *    disables the spin. For anything else, prefer the app-level `data-tip`
 *    delegated tooltip (`components/Tooltip.tsx`) — same visual recipe.
 *  - App call sites (INVENTORY §11): login `u={2} hover={false}` (28×28),
 *    header `u={1.5} hover={false}` (21×21). Neither passes `tip`.
 *  - The coin canvas is always 14u × 14u; sparkles deliberately paint OUTSIDE
 *    that box (`left:-4.5u`), so never wrap this in `overflow:hidden`.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';

const PAL: Record<string, string> = {
  O: 'var(--gt-gold-rim)',
  g: 'var(--gt-gold)',
  h: 'var(--gt-gold-lt)',
  d: 'var(--gt-gold-dk)',
  D: 'var(--gt-gold-deep)',
  H: 'var(--gt-gold-spark)',
};

// prettier-ignore
const F14 = ['.....OOOO.....', '...OOhhhhOO...', '..OhhhhhhggO..', '.OhhgggggggdO.', '.OhgggggggddO.', 'OhgDDDgDDDggdO', 'OggggDgDggggdO', 'OggDDDgDDDggdO', 'OggDgggggDgddO', '.OgDDDgDDDgdO.', '.OggggggggddO.', '..OgggggdddO..', '...OOddddOO...', '.....OOOO.....'];
// prettier-ignore
const S10a = ['...OOOO...', '.OOhhhhOO.', '.OhhhhggO.', 'OhhgggggdO', 'OhggggggdO', 'OhDDgDDgdO', 'OhgDgDggdO', 'OhDDgDDgdO', 'OhDgggDgdO', 'OgDDgDDgdO', 'OggggggddO', '.OggggddO.', '.OOddddOO.', '...OOOO...'];
// prettier-ignore
const S10b = ['...OOOO...', '.OOhhhhOO.', '.OhhhhggO.', 'OhhgggggdO', 'OhggggggdO', 'OhgDDgDDdO', 'OhggDgDgdO', 'OhgDDgDDdO', 'OhgDgggDdO', 'OggDDgDDdO', 'OggggggddO', '.OggggddO.', '.OOddddOO.', '...OOOO...'];
// prettier-ignore
const S6 = ['..OO..', '.OhhO.', '.OhgO.', 'OhggdO', 'OhggdO', 'OhggdO', 'OhggdO', 'OhggdO', 'OhggdO', 'OhggdO', 'OhggdO', '.OgdO.', '.OddO.', '..OO..'];
// prettier-ignore
const E2 = ['..', 'OO', 'hO', 'dO', 'hO', 'dO', 'hO', 'dO', 'hO', 'dO', 'hO', 'dO', 'OO', '..'];
// prettier-ignore
const SP5 = ['..h..', '..H..', 'hHHHh', '..H..', '..h..'];
// prettier-ignore
const SP3 = ['.h.', 'hHh', '.h.'];

/** hovered spin cycle — 6 frames × 90 ms = 540 ms/rev */
const SPIN = [F14, S10a, S6, E2, S6, S10b];
/** idle auto-spin, played at frames 84..89 of every 90-frame cycle */
const AUTO = [S10a, S6, E2, S6, S10b, F14];

interface PxOpts {
  canvas?: number;
  glint?: number | null;
}

/** `px()` from the DC source — sprite map → `<svg>` of 1×1 rects. */
function px(map: readonly string[], u: number, opts?: PxOpts): ReactElement {
  const rows = map.length;
  const cols = Math.max(...map.map((r) => r.length));
  const cw = opts?.canvas || cols;
  const off = Math.floor((cw - cols) / 2);
  const glint = opts?.glint;
  const rects: ReactElement[] = [];
  map.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      let ch = row[c];
      if (ch === undefined || ch === '.') continue;
      if (glint != null && ch !== 'O' && ch !== 'D') {
        const dg = c + off + r;
        if (dg >= glint && dg <= glint + 2) ch = ch === 'g' ? 'h' : ch === 'h' ? 'H' : ch === 'd' ? 'g' : ch;
      }
      rects.push(<rect key={r + '-' + c} x={c + off} y={r} width={1} height={1} fill={PAL[ch]} />);
    }
  });
  return (
    <svg
      viewBox={'0 0 ' + cw + ' ' + rows}
      width={cw * u}
      height={rows * u}
      shapeRendering="crispEdges"
      style={{ display: 'block' }}
    >
      {rects}
    </svg>
  );
}

function spk(u: number, x: number, y: number, size: number, key: string): ReactElement {
  return (
    <div key={key} style={{ position: 'absolute', left: x * u + 'px', top: y * u + 'px', pointerEvents: 'none' }}>
      {px(size === 2 ? SP5 : SP3, u)}
    </div>
  );
}

export interface QuarterIconProps {
  /** px per pixel cell — DC default 1.5 */
  u?: number | string;
  /** DC default true */
  sparkles?: boolean;
  /** UPPERCASE mono tooltip copy; DC default `''` */
  tip?: string;
  /** the ~8.1 s idle auto-spin — DC default true */
  auto?: boolean;
  /** hover-to-spin — DC default true; both app call sites pass `false` */
  hover?: boolean;
  /**
   * The coin as furniture (DESIGN-CHANGELOG 2026-09-20): no clock at all — no
   * glint, no twinkle, no auto-spin, no hover spin; the idle F14 face. For a
   * surface that shows a CROWD of quarters (SCHEDULE's PARTY table), where one
   * 90 ms interval each would be dozens of re-renders a tick. DC default false.
   */
  still?: boolean;
  /** host-wrapper style — positional properties only */
  style?: CSSProperties;
}

export function QuarterIcon({ u, sparkles, tip, auto, hover, still, style }: QuarterIconProps) {
  const [t, setT] = useState(0);
  const [hovRaw, setHovRaw] = useState(false);
  const isStill = !!still;

  useEffect(() => {
    if (isStill) return;
    const iv = setInterval(() => setT((x) => x + 1), 90);
    return () => clearInterval(iv);
  }, [isStill]);

  const uN = Number(u ?? 1.5);
  const hov = !isStill && (hover ?? true) && hovRaw;

  let map: readonly string[] = F14;
  let gl: number | null = null;
  if (isStill) {
    /* F14, no glint, no twinkle */
  } else if (hov) {
    map = SPIN[t % 6] ?? F14;
  } else {
    const k = t % 90;
    if ((auto ?? true) && k >= 84) {
      map = AUTO[k - 84] ?? F14;
    } else {
      const p = k % 30;
      gl = p < 10 ? p * 3 - 2 : null;
    }
  }

  const kids: ReactElement[] = [<div key="c">{px(map, uN, { canvas: 14, glint: gl })}</div>];
  if ((sparkles ?? true) && !isStill) {
    if (hov) {
      const s1 = twk(t, 9, 0);
      if (s1) kids.push(spk(uN, 11, -3.5, s1, 's1'));
    } else {
      const s1 = twk(t, 23, 3);
      const s2 = twk(t, 31, 17);
      if (s1) kids.push(spk(uN, 11, -3, Math.min(s1, 1), 's1'));
      if (s2) kids.push(spk(uN, -4.5, 7, Math.min(s2, 1), 's2'));
    }
  }

  const showTip = !!tip && hovRaw;

  return (
    // `display:flex` on the host: the DC runtime's wrapper is not a line box,
    // but a plain block div is, and the `inline-flex` coin inside it picked up
    // baseline descender space — so beside a `display:block` socket the coin
    // rode 2–3 px high in the quarter pockets (Marcus, staging 2026-08-27).
    <div className="sc-host" data-sc-name="Quarter Icon" style={{ display: 'flex', ...style }}>
      <div
        onMouseEnter={() => setHovRaw(true)}
        onMouseLeave={() => setHovRaw(false)}
        style={{ display: 'inline-flex', cursor: 'pointer', position: 'relative' }}
      >
        <div style={{ position: 'relative', width: 14 * uN + 'px', height: 14 * uN + 'px' }}>{kids}</div>
        {showTip ? (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '9px',
              background: 'var(--gt-card)',
              border: '1px solid var(--gt-border)',
              boxShadow: 'var(--gt-shadow-toast)',
              padding: '7px 9px 6px',
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '9px',
              letterSpacing: '.08em',
              color: 'var(--gt-text-3)',
              whiteSpace: 'nowrap',
              zIndex: 60,
              pointerEvents: 'none',
            }}
          >
            <span>{tip}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** `twk()` — 2 at phase 1, 1 at phases 0 and 2, otherwise dark. */
function twk(t: number, per: number, ph: number): number {
  const w = (t + ph) % per;
  return w === 1 ? 2 : w === 0 || w === 2 ? 1 : 0;
}
